import { useEffect, useRef, useState } from "react";
import i18next from "../../i18n";
import * as Yup from "yup";
import "yup-phone";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Button from "../../components/Button";
import { Helmet } from "react-helmet";
import { ReactTagManager } from 'react-gtm-ts';
import {useDropzone} from 'react-dropzone'


function Contact({ subject }) {
  const resultRef = useRef(null);
  const [isSubmitted, setisSubmitted] = useState(false);
  useEffect(() => { if (isSubmitted) resultRef.current?.focus(); }, [isSubmitted]);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [dropZoneError, setDropZoneError] = useState(false);
  const [submitError, setSubmitError] = useState("");

 const onDrop = (acceptedFiles, rejectedFiles) => {
  setDropZoneError(rejectedFiles.length > 0);
  const files = [];
  acceptedFiles.forEach((file) => {
    if(file.size > 1024 * 1024 * 10) {
      setDropZoneError(true);
    }else{
     files.push(file);
    }
  });
  setFiles(files);
 }
  const {getRootProps, getInputProps, isDragActive, rootRef: uploadRef} = useDropzone({onDrop, accept: {
    'application/pdf': [],
  }, maxFiles:1, multiple: false, disabled: isLoading})

  const yupValidation = Yup.object().shape({
    first_name: Yup.string().required(i18next.t("pages.contact.error-1")),
    last_name: Yup.string().required(i18next.t("pages.contact.error-1")),
    email: Yup.string().required(i18next.t("pages.contact.error-2")).email(),
  });
  const formOptions = { resolver: yupResolver(yupValidation) };
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm(formOptions);

  async function onSubmit(data) {
    if (isLoading) return;
    const isPdfReport = subject === "test-dokument-tilgjengelighet";
    if (isPdfReport && files.length !== 1) {
      setSubmitError(i18next.t("pages.contact.rate-my-pdf-required"));
      uploadRef.current?.focus();
      return;
    }
    setSubmitError("");
    const dataString =
      "Contact form - XPER Website \n"
      + (data.first_name ? "\nName: " + data.first_name : "")
      + (data.last_name ? " " + data.last_name : "")
      + (data.email ? "\nEmail: " + data.email : "")
      + (data.phone ? "\nPhone number: " + data.phone : "")
      + (data.company_name ? "\nCompany: " + data.company_name : "")
      + (data.company_website ? "\nCompany website : " + data.company_website : "")
      + (data.message ? "\nMessage : " + data.message : "");

    setIsLoading(true);
    try {
    // Convert selected files to base64 for SendGrid attachments
    const attachments = await Promise.all(
      (files || []).map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result || "";
              const base64 =
                typeof result === "string" ? result.split(",")[1] || "" : "";
              resolve({
                filename: file.name,
                type: file.type || "application/octet-stream",
                content: base64,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );
    const request = isPdfReport
      ? {
          url: "/api/pdfua-report",
          body: {
            ...data,
            language: i18next.language,
            file: attachments[0]
          }
        }
      : {
          url: "/api/contact",
          body: {
            subject:
              subject === "kontakt-oss"
                ? i18next.t("pages.contact.forms.contact.title")
                : subject === "book-demo"
                ? i18next.t("pages.contact.forms.demo.title")
                : subject === "snakk-med-ekspert"
                ? i18next.t("pages.contact.forms.expert.title")
                : i18next.t("pages.contact.forms.default.title"),
            body: dataString,
            attachments: attachments
          }
        };
    await fetch(request.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request.body),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || i18next.t("pages.contact.rate-my-pdf-submit-error"));
        }
        setisSubmitted(true);
        setIsLoading(false);
        ReactTagManager.action({
          event: 'contact_form_submitted',
          contact_form_submitted_subject: subject,
          contact_form_submitted_first_name: data.first_name,
          contact_form_submitted_last_name: data.last_name,
          contact_form_submitted_email: data.email,
          contact_form_submitted_phone: data.phone,
          contact_form_submitted_company: data.company_name,
          contact_form_submitted_company_website: data.company_website,
          contact_form_submitted_message: data.message,
        });
      })
      .catch((error) => {
        setSubmitError(error.message || i18next.t("pages.contact.rate-my-pdf-submit-error"));
        setIsLoading(false);
      });
    } catch (error) {
      setSubmitError(i18next.t("pages.contact.rate-my-pdf-submit-error"));
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50  px-4 sm:px-6 lg:px-8">
        <Helmet>
        <title>{subject === "kontakt-oss"
              ? i18next.t("pages.contact.forms.contact.title")
              : subject === "book-demo"
              ? i18next.t("pages.contact.forms.demo.title")
              : subject === "snakk-med-ekspert"
              ? i18next.t("pages.contact.forms.expert.title")
              : subject === "test-dokument-tilgjengelighet"
              ? i18next.t("pages.contact.forms.rate-my-pdf.title")
              : i18next.t("pages.contact.forms.default.title")}</title>
        <meta
          name="description"
          content={subject === "kontakt-oss"
              ? i18next.t("pages.contact.forms.contact.title")
              : subject === "book-demo"
              ? i18next.t("pages.contact.forms.demo.title")
              : subject === "snakk-med-ekspert"
              ? i18next.t("pages.contact.forms.expert.title")
              : i18next.t("pages.contact.forms.default.title")}
        />
        <link rel="canonical" href={`https://xperconsulting.no/skjema/${subject}`} />
      </Helmet>
      <div className="container max-w-xl pt-36 pb-12 justify-start items-start text-main-dark">
        <div className="w-full md:w-3/5 text-left  ">
          <h1 className="text-5xl font-bold text-main-dark mb-2">
            {subject === "kontakt-oss"
              ? i18next.t("pages.contact.forms.contact.title")
              : subject === "book-demo"
              ? i18next.t("pages.contact.forms.demo.title")
              : subject === "snakk-med-ekspert"
              ? i18next.t("pages.contact.forms.expert.title")
              : subject === "test-dokument-tilgjengelighet"
              ? i18next.t("pages.contact.forms.rate-my-pdf.title")
              : i18next.t("pages.contact.forms.default.title")}
          </h1>
          <p className="text-xl text-secondary mb-2">{subject === "test-dokument-tilgjengelighet" ? i18next.t("pages.contact.rate-my-pdf-description") : i18next.t("pages.contact.description")}</p>
          <p className="text-xl text-secondary mb-2">{subject === "test-dokument-tilgjengelighet" ? i18next.t("pages.contact.rate-my-pdf-description2") : i18next.t("pages.contact.description2")}</p>
         {subject === "test-dokument-tilgjengelighet" && <p className="text-xl text-secondary mb-0"> {i18next.t("pages.contact.rate-my-pdf-description3")}</p>}
        </div>

      </div>



      <div className="max-w-xl mx-auto text-left py-12">
        <p role="status" aria-atomic="true" className="text-main">{isLoading ? i18next.t("pages.contact.sending") : ""}</p>
        {!isSubmitted ? (
          (
            <div className="bg-main text-white w-full  rounded-lg p-12">
              <h2 className="text-3xl font-semibold text-main-900 mb-8 ">
                {i18next.t("pages.contact.fill-in-form")}
              </h2>

             {subject === "test-dokument-tilgjengelighet" &&
              <section className="max-w-xl mx-auto mb-8 text-center">
                    <input {...getInputProps({ "aria-label": i18next.t("pages.contact.rate-my-pdf.description"), "aria-describedby": "upload-help upload-feedback" })} />
                  <div
                    {...getRootProps({ role: "button", "aria-label": i18next.t("pages.contact.rate-my-pdf.description"), "aria-describedby": "upload-help upload-feedback", "aria-invalid": dropZoneError || Boolean(submitError) })}
                    className={`flex items-center justify-center w-1/2 mx-auto min-h-[100px] p-2 border-2 border-dashed rounded-lg transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-white ${
                      isDragActive
                        ? 'border-white bg-main-light text-white'
                        : 'border-white bg-main text-white hover:bg-main-dark hover:border-white'
                    }`}
                  >
                    <p className="text-center text-lg mb-0">
                      {i18next.t("pages.contact.rate-my-pdf.description")}
                    </p>
                  </div>
                  <p id="upload-help" className="text-sm mt-2">{i18next.t("pages.contact.upload-help")}</p>
                  <div id="upload-feedback" role="status" aria-atomic="true">
                  <ul className="list-disc list-inside text-white">
                    {files.map((file) => (
                      <li className="py-1" key={file.name}>{file.name}</li>
                    ))}
                  </ul>
                  {dropZoneError && (
                    <p className="text-red-200 mt-1 mb-0">{i18next.t("pages.contact.rate-my-pdf-error")}</p>
                  )}
                  </div>
                </section>
}
              <form
                noValidate
                aria-busy={isLoading}
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6 text-xl"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
                  <div>
                    <label className="block font-medium text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.first-name")}*
                      <input
                        type="text"
                        aria-label={i18next.t(
                          "pages.contact.input-fields.first-name"
                        )}
                        aria-required="true"
                        name="first_name"
                        autoComplete="given-name"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t("pages.contact.input-fields.first-name")
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.first_name ? "border-red-500" : ""
                        }`}
                        aria-invalid={Boolean(errors.first_name)}
                        aria-describedby={errors.first_name ? "first_name-error" : undefined}
                        {...register("first_name")}
                      />
                      {errors.first_name && (
                        <p id="first_name-error" role="alert" className="mt-1 mb-0 text-red-200">
                          {errors.first_name?.message}
                        </p>
                      )}
                    </label>
                  </div>

                  <div>
                    <label className="block  font-medium text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.last-name")}*
                      <input
                        type="text"
                        aria-label={i18next.t(
                          "pages.contact.input-fields.last-name"
                        )}
                        aria-required="true"
                        name="last_name"
                        autoComplete="family-name"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t("pages.contact.input-fields.last-name")
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.last_name ? "border-red-500" : ""
                        }`}
                        aria-invalid={Boolean(errors.last_name)}
                        aria-describedby={errors.last_name ? "last_name-error" : undefined}
                        {...register("last_name")}
                      />
                      {errors.last_name && (
                        <p id="last_name-error" role="alert" className="mt-1 mb-0 text-red-200">
                          {errors.last_name?.message}
                        </p>
                      )}
                    </label>
                  </div>

                  <div>
                    <label className="block  font-medium text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.email")}*
                      <input
                        type="email"
                        aria-label={i18next.t(
                          "pages.contact.input-fields.email"
                        )}
                        aria-required="true"
                        name="email"
                        autoComplete="email"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t("pages.contact.input-fields.email")
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.email ? "border-red-500" : ""
                        }`}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? "email-error" : undefined}
                        {...register("email")}
                      />
                      {errors.email && (
                        <p id="email-error" role="alert" className="mt-1 mb-0 text-red-200">
                          {errors.email?.message}
                        </p>
                      )}
                    </label>
                  </div>

                  <div>
                    <label className="block  font-medium text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.phone")}
                      <input
                        type="tel"
                        aria-label={i18next.t(
                          "pages.contact.input-fields.phone"
                        )}
                        aria-required="false"
                        name="phone"
                        autoComplete="tel"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t("pages.contact.input-fields.phone")
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.phone ? "border-red-500" : ""
                        }`}
                        {...register("phone")}
                      />
                    </label>
                  </div>

              {subject !== "test-dokument-tilgjengelighet" &&
                <>
                  <div>
                    <label className="block  font-medium text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.company-name")}
                      <input
                        type="text"
                        aria-label={i18next.t(
                          "pages.contact.input-fields.company-name"
                        )}
                        aria-required="false"
                        name="company_name"
                        autoComplete="organization"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t("pages.contact.input-fields.company-name")
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.company_name ? "border-red-500" : ""
                        }`}
                        {...register("company_name")}
                      />
                    </label>
                  </div>

                  <div>
                    <label className="block  font-medium text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.company-website")}
                      <input
                        type="text"
                        aria-label={i18next.t(
                          "pages.contact.input-fields.company-website"
                        )}
                        aria-required="false"
                        name="company_website"
                        autoComplete="url"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t(
                            "pages.contact.input-fields.company-website"
                          )
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.company_website ? "border-red-500" : ""
                        }`}
                        {...register("company_website")}
                      />
                    </label>
                  </div>
                </>}
                </div>
                {(subject === "kontakt-oss" ||
                  subject === "snakk-med-ekspert") && (
                  <div>
                    <label className="block text-main-700 mb-1">
                      {i18next.t("pages.contact.input-fields.message")}
                      <textarea
                        aria-label={i18next.t(
                          "pages.contact.input-fields.message"
                        )}
                        name="message"
                        readOnly={isLoading}
                        placeholder={
                          i18next.t("pages.contact.fill-in") +
                          i18next.t("pages.contact.input-fields.message")
                        }
                        className={`mt-1 text-lg text-main-dark placeholder:text-secondary block w-full rounded-md border-gray-300  p-2 sm: ${
                          errors.message ? "border-red-500" : ""
                        }`}
                        {...register("message")}
                      />
                    </label>
                  </div>
                )}
                <p className="text-sm text-main-600 mb-6 normal-case">
                  *{i18next.t("pages.contact.mandatory-text")}
                </p>
                {submitError && <p className="text-red-200 mb-4" role="alert">{submitError}</p>}
                <div className="flex justify-end">
                  <Button type="submit" disabled={isLoading} variant="solid" color="white" size="medium">
                    {i18next.t("pages.contact.send-button")}
                  </Button>
                </div>
              </form>
            </div>
          )
        ) : (
          <div className="bg-main text-white shadow-xl rounded-lg p-8 text-center max-w-xs mx-auto">
            <h2 ref={resultRef} tabIndex={-1} className="text-2xl font-semibold text-main-900 mb-4">
              {subject === "test-dokument-tilgjengelighet"
                ? i18next.t("pages.contact.rate-my-pdf-thanks")
                : i18next.t("pages.contact.thanks")}
            </h2>
            <p className="text-main-600">
              {subject === "test-dokument-tilgjengelighet"
                ? i18next.t("pages.contact.rate-my-pdf-thanks-under")
                : i18next.t("pages.contact.thanks-under")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Contact;
